import React from 'react';
import styled from 'styled-components';

// Import example JSON files
import SimpleWorkingExample from './examples/Resolver_SimpleWorking.json';
import SimpleNumbersExample from './examples/Resolver_SimpleNumbers.json';
import ComplexWorkingExample from './examples/Resolver_ComplexWorking.json';
import IndirectCircularExample from './examples/Resolver_IndirectCircularError.json';
import LongChainCircularExample from './examples/Resolver_LongChainIndirectCircularDependency.json';
import DirectSelfReferenceExample from './examples/Resolver_DirectSelfCircularReference.json';
import DropdownSimpleWorkingExample from './examples/Resolver_DropdownSimpleWorking.json';
import FormulaTemplateCollectionFieldExample from './examples/FormulaTemplate_CollectionField.json';
import FormulaTemplate_MultiSelectFieldExample from './examples/FormulaTemplate_MultiSelectField.json';
import FormulaTemplate_TableFieldExample from './examples/FormulaTemplate_TableField.json';
import FormulaTemplate_DropdownExample from './examples/FormulaTemplate_Dropdown.json';
import FormulaTemplate_TextareaFieldExample from './examples/FormulaTemplate_TextareaField.json';
import FormulaTemplate_SignatureFieldExample from './examples/FormulaTemplate_SignatureField.json';
import FormulaTemplate_Read_ChartFieldExample from './examples/FormulaTemplate_Read_ChartField.json';
import FormulaTemplate_Write_ChartFieldExample from './examples/FormulaTemplate_Write_ChartField.json';
import FormulaTemplate_DateFieldExample from './examples/FormulaTemplate_DateField.json';
import FormulaTemplate_BlockFieldExample from './examples/FormulaTemplate_BlockField.json';
import FormulaTemplate_ArithmeticExample from './examples/FormulaTemplate_Arithmetic.json';
import FormulaTemplate_EqualityOperatorExample from './examples/FormulaTemplate_EqualityOperator.json';
import FormulaTemplate_UnequalOperatorExample from './examples/FormulaTemplate_UnequalOperator.json';
import FormulaTemplate_GreaterThanOperatorExample from './examples/FormulaTemplate_GreaterThanOperator.json';
import FormulaTemplate_GreaterThanOrEqualOperatorExample from './examples/FormulaTemplate_GreaterThanOrEqualOperator.json';
import FormulaTemplate_LessThanOperatorExample from './examples/FormulaTemplate_LessThanOperator.json';
import FormulaTemplate_LessThanOrEqualOperatorExample from './examples/FormulaTemplate_LessThanOrEqualOperator.json';
import FormulaTemplate_ConditionalLogicExample from './examples/ConditionalLogic_FormulaTemplate.json';
import FormulaTemplate_ConditionalEvaluationCircularReference from './examples/FormulaTemplate_ConditionalEvaluationCircularReference.json';
import ImplicitCircularReferenceObjectArrayConstruction_FormulaTemplate from './examples/ImplicitCircularReferenceObjectArrayConstruction_FormulaTemplate.json';
import EncapsulatedCircularReference_FormulaTemplate from './examples/EncapsulatedCircularReference_FormulaTemplate.json';
import UndefinedValueReference_FormulaTemplateExample from './examples/UndefinedValueReference_FormulaTemplate.json';
import ReservedWordMisuse_FormulaTemplateExample from './examples/ReservedWordMisuse_FormulaTemplate.json';
import FormulaTemplate_TextFieldExample from './examples/FormulaTemplate_TextField.json';
import FormulaTemplate_NumberFieldExample from './examples/FormulaTemplate_NumberField.json';
import FormulaTemplate_FSMIndustryExample from './examples/FSMIndustry_FormulaTemplate.json';
// import FormulaTemplate_NFPA25_2020Example from './examples/FormulaTemplate_nfpa_25_2020.json';

const SelectContainer = styled.div`
  display: flex;
  align-items: center;
  
  label {
    font-size: 14px;
    font-weight: bold;
    margin-right: 8px;
    color: #333;
  }
`;

const StyledSelect = styled.select`
  font-size: 12px;
  padding: 8px;
  border: 1px solid rgb(209, 213, 215);
  border-radius: 6px;
  background-color: white;
  cursor: pointer;
  min-width: 200px;
  
  &:hover {
    border-color: #888888;
  }
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

// Example configurations
export const EXAMPLE_CONFIGS = {
  default: {
    name: 'Default (Remote)',
    data: null,
    description: 'Uses the remote data'
  },
  simpleWorking: {
    name: 'Simple Working',
    data: SimpleWorkingExample,
    description: 'Basic formula calculations with dependencies'
  },
  simpleNumbers: {
    name: 'Simple Numbers',
    data: SimpleNumbersExample,
    description: 'Basic formula calculations with dependencies'
  },
  complexWorking: {
    name: 'Complex Working',
    data: ComplexWorkingExample,
    description: 'Multi-level dependency chains'
  },
  indirectCircular: {
    name: 'Indirect Circular Error',
    data: IndirectCircularExample,
    description: 'Demonstrates circular dependency detection'
  },
  longChainCircular: {
    name: 'Long Chain Circular',
    data: LongChainCircularExample,
    description: 'Complex circular dependency chain'
  },
  directSelfReference: {
    name: 'Direct Self Reference',
    data: DirectSelfReferenceExample,
    description: 'Field referencing itself'
  },
  dropdownSimpleWorking: {
    name: 'Dropdown Simple Working',
    data: DropdownSimpleWorkingExample,
    description: 'Dropdown with simple working formula'
  },
  textField: {
    name: 'Text Field',
    data: FormulaTemplate_TextFieldExample,
    description: 'Formula template for text field'
  },
  numberField: {
    name: 'Number Field',
    data: FormulaTemplate_NumberFieldExample,
    description: 'Formula template for number field'
  },
  multiSelectField: {
    name: 'Multi Select Field',
    data: FormulaTemplate_MultiSelectFieldExample,
    description: 'Formula template for multi select field'
  },
  tableField: {
    name: 'Table Field',
    data: FormulaTemplate_TableFieldExample,
    description: 'Formula template for table field'
  },
  collectionField: {
    name: 'Collection Field',
    data: FormulaTemplateCollectionFieldExample,
    description: 'Formula template for collection field'
  },
  dropdownField: {
    name: 'Dropdown Field',
    data: FormulaTemplate_DropdownExample,
    description: 'Formula template for dropdown field'
  },
  textareaField: {
    name: 'Textarea Field',
    data: FormulaTemplate_TextareaFieldExample,
    description: 'Formula template for textarea field'
  },
  signatureField: {
    name: 'Signature Field',
    data: FormulaTemplate_SignatureFieldExample,
    description: 'Formula template for signature field'
  },
  readChartField: {
    name: 'Read Chart Field',
    data: FormulaTemplate_Read_ChartFieldExample,
    description: 'Formula template for read chart field'
  },
  writeChartField: {
    name: 'Write Chart Field',
    data: FormulaTemplate_Write_ChartFieldExample,
    description: 'Formula template for write chart field'
  },
  dateField: {
    name: 'Date Field',
    data: FormulaTemplate_DateFieldExample,
    description: 'Formula template for date field'
  },
  blockField: {
    name: 'Block Field',
    data: FormulaTemplate_BlockFieldExample,
    description: 'Formula template for block field'
  },
  arithmeticField: {
    name: 'Arithmetic Field',
    data: FormulaTemplate_ArithmeticExample,
    description: 'Formula template for arithmetic field'
  },
  equalityOperator: {
    name: 'Equality Operator',
    data: FormulaTemplate_EqualityOperatorExample,
    description: 'Formula template for equality operator'
  },
  unequalOperator: {
    name: 'Unequal Operator',
    data: FormulaTemplate_UnequalOperatorExample,
    description: 'Formula template for unequal operator'
  },
  greaterThanOperator: {
    name: 'Greater Than Operator',
    data: FormulaTemplate_GreaterThanOperatorExample,
    description: 'Formula template for greater than operator'
  },
  greaterThanOrEqualOperator: {
    name: 'Greater Than or Equal Operator',
    data: FormulaTemplate_GreaterThanOrEqualOperatorExample,
    description: 'Formula template for greater than or equal operator'
  },
  lessThanOperator: {
    name: 'Less Than Operator',
    data: FormulaTemplate_LessThanOperatorExample,
    description: 'Formula template for less than operator'
  },
  lessThanOrEqualOperator: {
    name: 'Less Than or Equal Operator',
    data: FormulaTemplate_LessThanOrEqualOperatorExample,
    description: 'Formula template for less than or equal operator'
  },
  conditionalLogic: {
    name: 'Conditional Logic',
    data: FormulaTemplate_ConditionalLogicExample,
    description: 'Formula template for conditional logic'
  },
  conditionalEvaluationCircularReference: {
    name: 'Conditional Evaluation Circular Reference',
    data: FormulaTemplate_ConditionalEvaluationCircularReference,
    description: 'Formula template for conditional evaluation circular reference'
  },
  implicitCircularReferenceObjectArrayConstruction: {
    name: 'Implicit Circular Reference Object Array Construction',
    data: ImplicitCircularReferenceObjectArrayConstruction_FormulaTemplate,
    description: 'Formula template for implicit circular reference object array construction'
  },
  encapsulatedCircularReference: {
    name: 'Encapsulated Circular Reference',
    data: EncapsulatedCircularReference_FormulaTemplate,
    description: 'Formula template for encapsulated circular reference'
  },
  undefinedValueReference: {
    name: 'Undefined Value Reference',
    data: UndefinedValueReference_FormulaTemplateExample,
    description: 'Formula template for undefined value reference'
  },
  reservedWordMisuse: {
    name: 'Reserved Word Misuse',
    data: ReservedWordMisuse_FormulaTemplateExample,
    description: 'Formula template for reserved word misuse'
  },
  fsmIndustry: {
    name: 'FSM Industry',
    data: FormulaTemplate_FSMIndustryExample,
    description: 'Formula template for FSM industry'
  },
  // nfpa25_2020: {
  //   name: 'NFPA 25 2020',
  //   data: FormulaTemplate_NFPA25_2020Example,
  //   description: 'Formula template for NFPA 25 2020'
  // }
};

/**
 * ExampleSelector component for choosing formula examples
 * 
 * @param {Object} props
 * @param {string} props.value - Currently selected example key
 * @param {function} props.onChange - Callback when selection changes (key, data) => void
 * @param {string} props.label - Label text for the selector
 */
const ExampleSelector = ({ 
  value = 'default', 
  onChange, 
  label = 'Example:' 
}) => {
  const handleChange = (event) => {
    const selectedKey = event.target.value;
    const selectedConfig = EXAMPLE_CONFIGS[selectedKey];
    
    if (onChange) {
      onChange(selectedKey, selectedConfig?.data || null);
    }
  };

  return (
    <SelectContainer>
      <StyledSelect 
        value={value} 
        onChange={handleChange}
        title={EXAMPLE_CONFIGS[value]?.description || ''}
      >
        {Object.entries(EXAMPLE_CONFIGS).map(([key, config]) => (
          <option key={key} value={key} title={config.description}>
            {config.name}
          </option>
        ))}
      </StyledSelect>
    </SelectContainer>
  );
};

export default ExampleSelector; 